import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";
import { Plus, Search, Edit, Trash2, Save, X, ArrowLeft } from "lucide-react";

const emptyForm = {
  product_name: "",
  hsn_code: "",
  sale_rate: 0,
  mrp: 0,
  gst_percent: 0,
  discount_type: "NONE",
  discount_value: 0,
  is_active: 1,
};

function Product() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  useEffect(() => {
    loadProducts(1);
  }, []);

  const authHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
    },
  });

  const loadProducts = async (page = 1) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API}/products`, {
        ...authHeader(),
        params: {
          page,
          per_page: pagination.per_page,
          search,
        },
      });

      const data = res.data.data;

      setProducts(data.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Product loading failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveProduct = async (e) => {
    e.preventDefault();

    if (!form.product_name.trim()) {
      Swal.fire("Required", "Please enter product name", "warning");
      return;
    }

    if (Number(form.sale_rate || 0) <= 0) {
      Swal.fire("Required", "Please enter sale rate", "warning");
      return;
    }

    try {
      const payload = {
        product_name: form.product_name.trim(),
        hsn_code: form.hsn_code || null,
        sale_rate: Number(form.sale_rate || 0),
        mrp: Number(form.mrp || 0),
        gst_percent: Number(form.gst_percent || 0),
        discount_type: form.discount_type || "NONE",
        discount_value: Number(form.discount_value || 0),
        is_active: Number(form.is_active),
      };

      if (editId) {
        await axios.put(`${API}/products/${editId}`, payload, authHeader());
        Swal.fire("Updated", "Product updated successfully", "success");
      } else {
        await axios.post(`${API}/products`, payload, authHeader());
        Swal.fire("Saved", "Product saved successfully", "success");
      }

      closeForm();
      loadProducts(pagination.current_page);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Something went wrong while saving product";

      Swal.fire("Error", msg, "error");
    }
  };

  const editProduct = (item) => {
    setEditId(item.id);

    setForm({
      product_name: item.product_name || "",
      hsn_code: item.hsn_code || "",
      sale_rate: item.sale_rate || 0,
      mrp: item.mrp || 0,
      gst_percent: item.gst_percent || 0,
      discount_type: item.discount_type || "NONE",
      discount_value: item.discount_value || 0,
      is_active: Number(item.is_active),
    });

    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteProduct = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Product?",
      text: "This product will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API}/products/${id}`, authHeader());
      Swal.fire("Deleted", "Product deleted successfully", "success");
      loadProducts(pagination.current_page);
    } catch (error) {
      Swal.fire("Error", "Product delete failed", "error");
    }
  };

  return (
    <AppLayout
      title={showForm ? (editId ? "Edit Product" : "Add Product") : "Product Master"}
      subtitle={
        showForm
          ? "Enter product details for billing"
          : "Manage product details"
      }
    >
      {showForm ? (
        <div className="product-table-page">
          <div className="product-inline-card product-form-only-card">
            <div className="product-inline-header">
              <div>
                <h3>{editId ? "Update Product" : "Add Product"}</h3>
                <p>
                  {editId
                    ? "Update existing product information"
                    : "Create new product for billing"}
                </p>
              </div>

              <button className="product-inline-close" onClick={closeForm}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={saveProduct}>
              <div className="product-inline-grid">
                <div className="eb-field">
                  <label>Product Name *</label>
                  <input
                    name="product_name"
                    value={form.product_name}
                    onChange={handleChange}
                    placeholder="Enter product name"
                  />
                </div>

                <div className="eb-field">
                  <label>HSN Code Optional</label>
                  <input
                    name="hsn_code"
                    value={form.hsn_code}
                    onChange={handleChange}
                    placeholder="Enter HSN code"
                  />
                </div>

                <div className="eb-field">
                  <label>Sale Rate *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="sale_rate"
                    value={form.sale_rate}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="eb-field">
                  <label>MRP</label>
                  <input
                    type="number"
                    step="0.01"
                    name="mrp"
                    value={form.mrp}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="eb-field">
                  <label>GST %</label>
                  <input
                    type="number"
                    step="0.01"
                    name="gst_percent"
                    value={form.gst_percent}
                    onChange={handleChange}
                    placeholder="0"
                  />
                </div>

                <div className="eb-field">
                  <label>Discount Type</label>
                  <select
                    name="discount_type"
                    value={form.discount_type}
                    onChange={handleChange}
                  >
                    <option value="NONE">None</option>
                    <option value="PERCENT">Percent</option>
                    <option value="AMOUNT">Amount</option>
                  </select>
                </div>

                <div className="eb-field">
                  <label>Discount Value</label>
                  <input
                    type="number"
                    step="0.01"
                    name="discount_value"
                    value={form.discount_value}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="eb-field">
                  <label>Status</label>
                  <select
                    name="is_active"
                    value={form.is_active}
                    onChange={handleChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>
              </div>

              <div className="product-inline-actions">
                <button
                  type="button"
                  className="product-cancel-btn"
                  onClick={closeForm}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                <button type="submit" className="product-save-btn">
                  <Save size={18} />
                  {editId ? "Update Product" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="product-table-page">
          <div className="product-toolbar">
            <div className="product-search-box">
              <Search size={20} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadProducts(1);
                }}
                placeholder="Search Products"
              />
            </div>

            <button className="product-add-btn" onClick={openAddForm}>
              <Plus size={20} />
              ADD PRODUCT
            </button>
          </div>

          <div className="product-table-card">
            <div className="product-table-wrapper">
              <table className="product-simple-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product Name</th>
                    <th>Sale Rate</th>
                    <th>MRP</th>
                    <th>GST</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="7" className="product-empty-row">
                        Loading products...
                      </td>
                    </tr>
                  ) : products.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="product-empty-row">
                        No products found
                      </td>
                    </tr>
                  ) : (
                    products.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.product_name}</td>
                        <td>₹{Number(item.sale_rate || 0).toFixed(2)}</td>
                        <td>₹{Number(item.mrp || 0).toFixed(2)}</td>
                        <td>{Number(item.gst_percent || 0).toFixed(2)}%</td>
                        <td>
                          <span
                            className={
                              Number(item.is_active)
                                ? "product-status-active"
                                : "product-status-inactive"
                            }
                          >
                            {Number(item.is_active) ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="product-action-btn edit"
                            onClick={() => editProduct(item)}
                          >
                            <Edit size={22} />
                          </button>

                          <button
                            className="product-action-btn delete"
                            onClick={() => deleteProduct(item.id)}
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="product-pagination">
              <span>
                {products.length === 0
                  ? "0–0"
                  : `${(pagination.current_page - 1) * pagination.per_page + 1}–${
                      (pagination.current_page - 1) * pagination.per_page +
                      products.length
                    }`}{" "}
                of {pagination.total}
              </span>

              <button
                disabled={pagination.current_page <= 1}
                onClick={() => loadProducts(pagination.current_page - 1)}
              >
                ‹
              </button>

              <button
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() => loadProducts(pagination.current_page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default Product;